import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useNavigate, useLocation } from 'react-router-dom';
import './Navbar.css';
import logo from "../../assets/logo.svg";
import { buildApiUrl } from "../../config/api.config";

const Navbar = (props) => {
  let location = useLocation();
  let navigate = useNavigate();
  const [userDetails, setUserDetails] = useState({ name: "", email: "", id: "", role: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const navbarCollapseRef = useRef(null);

  const fetchUserDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsLoading(false);
        return null;
      }
      
      const response = await fetch(buildApiUrl("users/getuser"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "auth-token": token,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("userDetails", JSON.stringify(data));
        return data;
      } else {
        setIsLoading(false);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user details:", error.message);
      setIsLoading(false);
      return null;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      setScrolled(offset > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    
    const getUserDetails = async () => {
      const userData = await fetchUserDetails();
      if (userData) {
        setUserDetails(userData);
      } else {
        setUserDetails({ name: "", email: "", id: "", role: "" });
      }
      setIsLoading(false);
    };

    const storedUserDetails = JSON.parse(localStorage.getItem("userDetails"));
    if (storedUserDetails) {
      setUserDetails(storedUserDetails);
      setIsLoading(false);
    } else {
      getUserDetails();
    }

    const handleIdentityUpdate = () => {
       getUserDetails();
    };
    window.addEventListener('identityUpdate', handleIdentityUpdate);
    
    return () => {
      window.removeEventListener('identityUpdate', handleIdentityUpdate);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const logOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userDetails');
    setUserDetails({ name: "", email: "", id: "", role: "" });
    props.showAlert("Logout Successfully", "success");
    navigate("/");
  };

  const handleAdminClick = () => {
    props.showAlert("Come to admin panel", "success");
  };

  const closeNavbar = () => {
    if (navbarCollapseRef.current && window.innerWidth <= 768) {
      const collapse = window.bootstrap.Collapse.getInstance(navbarCollapseRef.current);
      if (collapse) {
        collapse.hide();
      }
    }
  };

  function getInitials(name) {
    if (!name) return "";
    const nameArray = name.split(" ");
    const initials = nameArray.map(part => part[0]).join("");
    return initials.toUpperCase();
  }

  const isHomePage = location.pathname === "/";

  return (
    <nav className={`navbar navbar-expand-lg fixed-top ${(!isHomePage || scrolled) ? 'navbar-scrolled' : ''}`}>
      <div className="container">
        <Link className="navbar-brand" to="/">
          <img src={logo} alt="TastyFlow" className="navbar-logo" />
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarSupportedContent" ref={navbarCollapseRef}>
          <ul className="navbar-nav mx-auto">
            <li className="nav-item">
              <Link className={`nav-link ${location.pathname === "/" ? "active" : ""}`} to="/" onClick={closeNavbar}>Home</Link>
            </li>
            <li className="nav-item">
              <Link className={`nav-link ${location.pathname === "/About" ? "active" : ""}`} to="/About" onClick={closeNavbar}>About</Link>
            </li>
            <li className="nav-item">
              <Link className={`nav-link ${location.pathname === "/Menu_Page" ? "active" : ""}`} to="/Menu_Page" onClick={closeNavbar}>Menu</Link>
            </li>
            <li className="nav-item">
              <Link className={`nav-link ${location.pathname === "/all-foods" ? "active" : ""}`} to="/all-foods" onClick={closeNavbar}>Recipes</Link>
            </li>
          </ul>

          <div className="navbar-actions d-flex align-items-center">
            {!isLoading && (
              <>
                {userDetails.role !== 'admin' && (
                  <>
                    {localStorage.getItem('currentRestaurantId') && (
                      <Link to="/select-restaurant" className="restaurant-pill" onClick={closeNavbar}>
                        <div className="pill-avatar">
                          {localStorage.getItem('currentRestaurantLogo') ? (
                            <img src={localStorage.getItem('currentRestaurantLogo')} alt="Logo" />
                          ) : (
                            <span>{localStorage.getItem('currentRestaurantName')?.charAt(0) || 'R'}</span>
                          )}
                        </div>
                        <span className="pill-name">{localStorage.getItem('currentRestaurantName') || 'Restaurant'}</span>
                        <i className="fa-solid fa-chevron-down"></i>
                      </Link>
                    )}
                    <Link to="/table-reserve" className="reserve-btn-link">
                      <button className="reserve-btn" type="button">Book a Table</button>
                    </Link>
                  </>
                )}

                {localStorage.getItem("token") ? (
                  <div className="user-control-group d-flex align-items-center">
                    {userDetails.role === 'admin' && (
                      <Link to="/admin" className="admin-link-btn" onClick={() => { handleAdminClick(); closeNavbar(); }}>
                        Admin Portal
                      </Link>
                    )}
                    <button className="logout-icon-btn" onClick={logOut} title="Logout">
                      <i className="fa-solid fa-right-from-bracket"></i>
                    </button>
                    <Link className="profile-initial-link" to="/info" onClick={closeNavbar}>
                      {getInitials(userDetails.name)}
                    </Link>
                  </div>
                ) : (
                  <div className="auth-group d-flex">
                    <Link className="login-link" to="/login" onClick={closeNavbar}>Login</Link>
                    <Link className="signup-btn" to="/signup" onClick={closeNavbar}>Sign Up</Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
